import { useMemo, useState } from "react";
import { Box, Button, Checkbox, Flex, Popover, Text } from "@radix-ui/themes";

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  width?: number;
  disabled?: boolean;
}

export function FieldMultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select fields...",
  width = 472,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);

  const summary = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length <= 3) return value.join(", ");
    return `${value.slice(0, 2).join(", ")} +${value.length - 2} more`;
  }, [value, placeholder]);

  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          type="button"
          variant="surface"
          disabled={disabled}
          style={{ width: "100%", justifyContent: "space-between", fontWeight: "normal" }}
        >
          <Text size="2" truncate>{summary}</Text>
          <Text size="1" color="gray">▾</Text>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        style={{ width, padding: 0, zIndex: 12000, overflow: "visible", pointerEvents: "auto" }}
        container={typeof document !== "undefined" ? document.body : undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Box style={{ maxHeight: 280, overflowY: "auto", padding: 4 }}>
          {options.length === 0 ? (
            <Text size="2" color="gray" style={{ display: "block", padding: 8 }}>
              No fields
            </Text>
          ) : (
            options.map((opt) => {
              const checked = value.includes(opt.value);
              return (
                <Flex
                  key={opt.value}
                  align="center"
                  gap="2"
                  onClick={() => toggle(opt.value)}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: 13,
                    backgroundColor: checked ? "var(--accent-3)" : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!checked) e.currentTarget.style.backgroundColor = "var(--gray-4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = checked ? "var(--accent-3)" : "";
                  }}
                >
                  <Checkbox checked={checked} />
                  <Text size="2">{opt.label}</Text>
                </Flex>
              );
            })
          )}
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}
